import { Children, useEffect, useRef, useState } from 'react';
import { useSwiper } from 'swiper/react';
import { Check } from '@vfit/shared-icons';
import { useRouter } from 'next/router';
import { ImageAtoms, CustomText, Price } from '@vfit/shared/atoms';
import {
  getProductCategory,
  getRedirectCoverage,
  IN_ACTIVATION,
  redirectToShoppingCartFromReverseAddress,
  retrieveInTest,
  trackActionByActionType,
  usePage,
} from '@vfit/consumer/data-access';
import { useRedirectCoverageToolFlow } from '@vfit/consumer/hooks';
import { useQueryClient } from 'react-query';
import { removeCurrency, useDeviceType } from '@vfit/shared/hooks';
import {
  getClickToCall,
  IProductLead,
  linkInAppOpener,
  LoggerInstance,
  trackLink,
  useTracking,
} from '@vfit/shared/data-access';
import {
  ButtonVariant,
  IActionType,
  IErrorFormApi,
  IGradientImage,
  ISubmitOutput,
} from '@vfit/shared/models';
import { Inflation, Pills, VfModal } from '@vfit/shared/components';
import { fonts, purify } from '@vfit/shared/themes';
import {
  IGetAllCmsForm,
  manageErrorLeadPlatform,
  manageSuccessLeadPlatform,
  openLeadModal,
} from '@vfit/shared/data-access';
import { energyManager, SoftManagerService } from '@vfit/consumer/providers';
import { ButtonCBU, CoverageToolModal } from '@vfit/consumer/components';
import {
  ModalContent,
  ModalTitle,
  ProductCardBottom,
  ProductCardBottomContainer,
  ProductCardBottomContainerText,
  ProductCardContainer,
  ProductCardOfferLabel,
  ProductCardSpecContainer,
  ProductCardSpecItem,
  ProductCardSpecItemAction,
  ProductCardImageContainer,
  ProductCardSpecTitle,
  ProductCardTopImage,
  ProductCardTopImageContent,
  DivImgContainer,
  H3Header,
  H2Header,
} from './productCard.style';
import { IProductCardProps } from './productCard.models';
import ProductCardLoader from './components/ProductCardLoader/productCardLoader';
import { Svg } from '@vfit/shared/atoms';

const ProductCard = ({
  noMargin = false,
  isLoading = false,
  product: originalProduct,
  large,
  zoomIn,
  onActive,
  hideActions,
  setSpecHeight,
  classKey,
  showCase,
  onTrack,
  cardIndex,
  haveMarketingLabel,
  tabName,
  hideSpecs,
  isShoppingCart,
}: IProductCardProps) => {
  const { silentLoginUser } = usePage();
  const { asPath, push } = useRouter();
  const swiper = useSwiper();
  const specRef = useRef<HTMLDivElement | null>(null);
  const isInTest = retrieveInTest() && originalProduct?.enableAbTest;
  const abTestConfiguration = originalProduct.offerDetailABTest || {};
  const product = !isInTest
    ? originalProduct
    : {
        ...originalProduct,
        ...abTestConfiguration,
      };
  const {
    title,
    titleHtml,
    label,
    image,
    price,
    priceHtml,
    originalPrice,
    recurrence,
    subprice,
    description,
    hasEmptySpace,
    action,
    hubSecondAction,
    buttons,
    specs: specsProduct,
    pills,
    slug,
    category,
    queryParamGoDetail,
    inflactionHub,
    inflactionCartView,
    isGlobalPromoActive,
    cmsId,
    offerLockInWeb,
  } = product;
  const [purifiedTitleHtml] = purify([titleHtml || title || '']);
  const [isShowCoverageTool, setIsShowCoverageTool] = useState(false);
  const [isInActivation] = useRedirectCoverageToolFlow(cmsId || '');
  const { isMobile } = useDeviceType();
  const [modal, setModal] = useState({
    show: false,
    text: '',
    title: '',
  });
  const detailLabel = buttons && buttons?.length > 0 ? buttons?.[0].label || '' : '';
  const queryClient = useQueryClient();

  const { trackWithNewDatalayer } = useTracking({
    event: ['product_view'],
    disableInitialTrack: true,
  });

  const handlerTracking = () => {
    if (typeof cardIndex === 'number' && typeof showCase === 'number' && onTrack)
      LoggerInstance.debug('Handler track');
  };

  useEffect(() => {
    if (isMobile && swiper) {
      setTimeout(() => {
        swiper?.slideTo(0);
      }, 500);
    }
  }, [tabName]);

  useEffect(() => {
    if (!isShowCoverageTool && isInActivation) {
      setIsShowCoverageTool(true);
    }
  }, [isInActivation]);

  useEffect(() => {
    if (specRef?.current?.offsetHeight && setSpecHeight) {
      setSpecHeight(specRef.current?.offsetHeight);
    }
  }, [setSpecHeight, specRef]);

  const goDetail = () => {
    trackActionByActionType(
      {
        type: action?.type,
        title: detailLabel,
      },
      product,
    );
    handlerTracking();
    if (slug) {
      const pathWithoutParams = asPath.split('?')[0];
      const queryParamsDetail =
        queryParamGoDetail && queryParamGoDetail?.length > 0 ? queryParamGoDetail.join('&') : null;
      let href = `${pathWithoutParams}/${slug}`;
      if (queryParamsDetail) {
        href += `?${queryParamsDetail}`;
      }
      window.location.href = href;
    }
  };

  const checkLoading = () => {
    return action?.type === IActionType.ENERGY_FUNNEL && !silentLoginUser?.isReady;
  };

  const activeNow = () => {
    trackActionByActionType(
      {
        type: action?.type,
        title: action?.title,
      },
      product,
    );
    handlerTracking();
    if (onActive) {
      onActive();
      return;
    }
    let allLeads;
    let refLead;
    let lead;
    switch (action?.type) {
      case IActionType.CUSTOM_URL:
        if (action?.url) {
          linkInAppOpener(action.url, action?.isBlank === 'true' ? '_blank' : '_self');
        }
        break;
      case IActionType.CALL_ME_NOW:
        getClickToCall(queryClient, {
          product,
          idClickToCall: action?.key,
          clickToCallFallbackUrl: action?.callMeNow || action?.url,
        });
        break;
      case IActionType.CALL_ME_NOW_SUNRISE:
        if (!action?.title || !action?.url) return;
        getClickToCall(queryClient, {
          product,
          idClickToCall: action?.key,
          clickToCallFallbackUrl: action?.callMeNow || action?.url,
          clickToCallFallbackType: 'CTC_SUNRISE',
          clickToCallFallbackTemplate: action?.template,
        });
        break;
      case IActionType.COVERAGE_TOOL:
        setIsShowCoverageTool(true);
        break;
      case IActionType.AUTO_COVERAGE:
        if (cmsId) redirectToShoppingCartFromReverseAddress(cmsId);
        break;
      case IActionType.CHECKOUT_MODAL_MOBILE:
        localStorage.setItem('tProd', JSON.stringify(product));
        window.location.href = getRedirectCoverage();
        break;
      case IActionType.COVERAGE_TOOL_WITH_REDIRECT:
        if (action?.url && product?.cmsId) {
          const alreadyHaveParams = action.url.includes('?');
          window.location.href = alreadyHaveParams
            ? `${action.url}&${IN_ACTIVATION}=${product?.cmsId || ''}`
            : `${action.url}?${IN_ACTIVATION}=${product?.cmsId || ''}`;
        }
        break;
      case IActionType.LEAD_MODAL:
        allLeads = queryClient?.getQueryData('getAllLeads') as IGetAllCmsForm;
        // form name
        refLead = action?.localUrl || '';
        lead = allLeads[refLead];
        if (lead?.forms) {
          openLeadModal({
            form: lead.forms,
            onSuccess: (submitOutput: ISubmitOutput) =>
              manageSuccessLeadPlatform(submitOutput, push, queryClient),
            onError: (submitOutput: ISubmitOutput[], errorCmsApi?: IErrorFormApi) =>
              manageErrorLeadPlatform(submitOutput, errorCmsApi, push, queryClient),
          });
        }
        break;
      case IActionType.ENERGY_FUNNEL:
        energyManager.handleEnergyManager({
          product,
          showModal: true,
        });
        break;
      default:
        break;
    }
  };

  const handleHubAction = () => {
    const type = hubSecondAction?.type ? +hubSecondAction.type : -1;
    let allLeads;
    let refLead;
    let lead;
    trackActionByActionType(
      {
        type,
        title: hubSecondAction?.title,
      },
      product,
    );
    switch (type) {
      case IActionType.CUSTOM_URL:
        if (hubSecondAction?.url)
          linkInAppOpener(
            hubSecondAction.url,
            hubSecondAction?.isBlank?.toString() === 'true' ? '_blank' : '_self',
          );
        break;
      case IActionType.CALL_ME_NOW:
        getClickToCall(queryClient, {
          product,
          idClickToCall: hubSecondAction?.key,
          clickToCallFallbackUrl: hubSecondAction?.callMeNow || hubSecondAction?.url,
        });
        break;
      case IActionType.CALL_ME_NOW_SUNRISE:
        if (!hubSecondAction?.title || !hubSecondAction?.url) return;
        getClickToCall(queryClient, {
          product,
          idClickToCall: hubSecondAction?.key,
          clickToCallFallbackUrl: hubSecondAction?.callMeNow || hubSecondAction?.url,
          clickToCallFallbackType: 'CTC_SUNRISE',
          clickToCallFallbackTemplate: hubSecondAction?.template,
        });
        break;
      case IActionType.LEAD_MODAL:
        allLeads = queryClient?.getQueryData('getAllLeads') as IGetAllCmsForm;
        // form name
        refLead = hubSecondAction?.localUrl || '';
        lead = allLeads[refLead];
        if (lead?.forms) {
          openLeadModal({
            form: lead.forms,
            product: SoftManagerService(queryClient).getPageProduct(product) as IProductLead,
            onSuccess: manageSuccessLeadPlatform,
            onError: manageErrorLeadPlatform,
          });
        }
        break;
      default:
        break;
    }
  };

  const hideCoverageTool = () => {
    const pageDatalayer: any = queryClient.getQueryData('pageDatalayer');
    if (pageDatalayer) {
      trackWithNewDatalayer({
        event: pageDatalayer?.event,
        event_label: pageDatalayer?.event_label,
        opts: pageDatalayer?.opts,
        pageProduct: pageDatalayer?.pageProduct,
        cartProduct: pageDatalayer?.cartProduct,
        visitorTrackingOpts: pageDatalayer?.visitorTrackingOpts,
      });
    }
    setIsShowCoverageTool(false);
  };

  const openModal = (item) => {
    trackLink(
      'product_card_more_info',
      undefined,
      undefined,
      undefined,
      undefined,
      '',
      '',
      '',
      '',
      '',
      {
        ...(product?.offerType && {
          product_category: getProductCategory(product?.offerType || ''),
        }),
        ...(product?.offerId && { product_id: product?.offerId?.toString() || '' }),
        ...(product?.slug && { product_name: product?.slug || '' }),
        ...(product?.price && { product_price: removeCurrency(product?.price || '') }),
      },
    );
    setModal({
      show: true,
      text: item.action || '',
      title: item?.titleOverlay || item.title || '',
    });
  };

  const renderSpecs = () => {
    const shortCharacteristicsLockIn = offerLockInWeb?.lockInMM?.shortCharacteristicsLockIn
      ? [offerLockInWeb.lockInMM.shortCharacteristicsLockIn]
      : [];
    const specs = product?.isLockInMMProduct ? shortCharacteristicsLockIn : specsProduct;
    return (
      <div
        ref={specRef}
        className={`product-specs-${classKey}`}
        style={{
          marginBottom: 32,
        }}
      >
        {Children.toArray(
          specs &&
            specs.length > 0 &&
            specs.map((spec) => {
              const { title: specTitle, items } = spec;

              return (
                <div>
                  {specTitle && <ProductCardSpecTitle>{specTitle}</ProductCardSpecTitle>}
                  {items &&
                    items.length > 0 &&
                    Children.toArray(
                      items
                        .filter((item) => !!item.title)
                        .map((item) => (
                          <ProductCardSpecContainer>
                            <ProductCardSpecItem isTitle={item?.isTitleGroup}>
                              {!item?.isTitleGroup &&
                                (item?.icon ? (
                                  <ProductCardImageContainer>
                                    <ImageAtoms
                                      width="16px"
                                      nameCard={item.title}
                                      image={item?.icon}
                                    />
                                  </ProductCardImageContainer>
                                ) : (
                                  <div>
                                    <Check />
                                  </div>
                                ))}
                              <CustomText text={item.title} textAlign="left" />
                            </ProductCardSpecItem>
                            {item.action && (
                              <div role="presentation" onClick={() => openModal(item)}>
                                <ProductCardSpecItemAction>
                                  <Svg name="info" height={16} width={16} />
                                </ProductCardSpecItemAction>
                              </div>
                            )}
                          </ProductCardSpecContainer>
                        )),
                    )}
                </div>
              );
            }),
        )}
      </div>
    );
  };

  const renderInflation = () => {
    if (isShoppingCart && inflactionCartView && inflactionCartView[0]?.text) {
      return Children.toArray(
        inflactionCartView.map((infationCart) => (
          <Inflation
            InflationTitle={infationCart?.text}
            popup={infationCart?.popup}
            popupTitle={infationCart?.titlePopup}
            color={infationCart?.color}
            inflationDisclaimer={infationCart?.disclaimer}
            isGlobalPromo={infationCart?.isGlobalPromo}
            tracking={() => {
              trackActionByActionType(
                {
                  type: IActionType.SHOW_DETAIL,
                  title: infationCart?.text,
                },
                product,
              );
            }}
            isConsumer={true}
          />
        )),
      );
    }
    if (inflactionHub && inflactionHub?.length > 0 && inflactionHub?.some((e) => e.text)) {
      return Children.toArray(
        inflactionHub.map((inflation) => (
          <Inflation
            InflationTitle={inflation?.text}
            popup={inflation?.popup}
            popupTitle={inflation?.titlePopup}
            color={inflation?.color}
            isGlobalPromo={inflation?.isGlobalPromo}
            tracking={() => {
              trackActionByActionType(
                {
                  type: IActionType.SHOW_DETAIL,
                  title: inflation?.text,
                },
                product,
              );
            }}
            isConsumer={true}
          />
        )),
      );
    }

    return undefined;
  };

  const renderPrice = () => (
    <>
      {(price || recurrence || title) && (
        <div className="productTitle">
          <Price
            amount={priceHtml ? priceHtml : price}
            recurrence={recurrence}
            oldAmount={originalPrice}
          />
        </div>
      )}
      {subprice && <h4>{subprice || '\u00A0'}</h4>}
      {description && (
        <div className="productDescription">
          <CustomText
            text={description}
            textAlign="left"
            fontFamily={fonts.regular}
            size={14}
            lineHeight={18}
          />
        </div>
      )}
      {hasEmptySpace && <div style={{ marginBottom: '60px' }} />}
      {(!isGlobalPromoActive || !isShoppingCart) && renderInflation()}
    </>
  );

  const renderButtons = () => (
    <div
      style={{
        marginBottom: 32,
      }}
    >
      {action?.type && action.title && (
        <div
          style={{
            marginBottom: 20,
          }}
        >
          <ButtonCBU
            loading={checkLoading()}
            variant={'primary'}
            onClick={activeNow}
            name={`action_card_${!cardIndex ? '1' : cardIndex + 1}_${
              product?.offerId?.toString() || ''
            }`}
          >
            {action.title}
          </ButtonCBU>
        </div>
      )}
      {hubSecondAction?.type && hubSecondAction.title && (
        <div
          style={{
            marginBottom: 20,
          }}
        >
          <ButtonCBU
            variant={hubSecondAction?.style?.toString() as ButtonVariant}
            onClick={handleHubAction}
            name={`secondAction_card_${!cardIndex ? '1' : cardIndex + 1}_${
              product?.offerId?.toString() || ''
            }`}
          >
            {hubSecondAction.title}
          </ButtonCBU>
        </div>
      )}
      {detailLabel && (
        <ButtonCBU
          variant={'secondary'}
          onClick={goDetail}
          name={`detailAction_card_${!cardIndex ? '1' : cardIndex + 1}_${
            product?.offerId?.toString() || ''
          }`}
        >
          {detailLabel}
        </ButtonCBU>
      )}
    </div>
  );

  const clickImage = () => {
    if (!product.disableClickDetail) goDetail();
  };

  return (
    <>
      {haveMarketingLabel && (
        <ProductCardOfferLabel className="marketing-label" haveLabel={!!product.marketingLabel}>
          {product.marketingLabel && <span>{product.marketingLabel}</span>}
        </ProductCardOfferLabel>
      )}
      <ProductCardContainer noMargin={noMargin} haveOfferLabel={haveMarketingLabel}>
        {isLoading && <ProductCardLoader />}
        {!isLoading && (
          <>
            <ProductCardTopImage
              large={large}
              isClickable={!product?.disableClickDetail || !product?.isLockInMMProduct}
              onClick={clickImage}
              key={Math.random().toString()}
            >
              <Pills pills={pills || []} />
              <ProductCardTopImageContent>
                <H2Header>{category}</H2Header>
                <H3Header dangerouslySetInnerHTML={{ __html: purifiedTitleHtml || '' }} />
              </ProductCardTopImageContent>
              <DivImgContainer absolute zoomIn={zoomIn}>
                <ImageAtoms
                  gradient={IGradientImage.GRADIENT_BOTTOM_40}
                  nameCard="topImage"
                  image={image}
                />
              </DivImgContainer>
            </ProductCardTopImage>
            <ProductCardBottom>
              <ProductCardBottomContainer>
                <ProductCardBottomContainerText>
                  {isMobile && label && <h6>{label}</h6>}
                  {!isMobile && label && <h6>{label || '\u00A0'}</h6>}
                  {renderPrice()}
                </ProductCardBottomContainerText>
                <div>{!hideActions && renderButtons()}</div>
                <div>{!hideSpecs && renderSpecs()}</div>
                <div>{isGlobalPromoActive && isShoppingCart && renderInflation()}</div>
              </ProductCardBottomContainer>
            </ProductCardBottom>
          </>
        )}
        <VfModal
          height={563}
          isOpen={modal.show}
          handleClose={() => setModal({ show: false, text: '', title: '' })}
        >
          <>
            {modal.title && (
              <ModalTitle>
                <CustomText fontFamily={fonts.exbold} textAlign="left" text={modal.title} />
              </ModalTitle>
            )}
            <ModalContent>
              <CustomText size={18} lineHeight={24} textAlign="left" text={modal.text} />
            </ModalContent>
          </>
        </VfModal>
        <CoverageToolModal
          backgroundImage={product?.image}
          isOpen={isShowCoverageTool}
          product={product}
          handleClose={hideCoverageTool}
        />
      </ProductCardContainer>
    </>
  );
};

export default ProductCard;

